import { useMemo, useState } from 'react';
import { BarChartDataItem, ChartColors, MappingList, MappingListItem } from '@silverfort/sf-components';
import { QueryFilters } from '../api';

import { getDateShortFormat } from '../../../utils/dates';
import { AllAuthenticationsTraffic, AuthenticationsTrafficTab } from '../types';
import useUniversalMfaMetrics from './useUniversalMfaMetrics';

interface UseAuthenticationsTrafficBarChartProps {
    filters: QueryFilters;
}

interface UseAuthenticationsTrafficBarChartReturn {
    selectedTab: AuthenticationsTrafficTab;
    setSelectedTab: (tab: AuthenticationsTrafficTab) => void;
    barChartData: BarChartDataItem[];
    isLoading: boolean;
    barChartVariant: 'single' | 'stacked';
    mappingList: MappingListItem[];
}

export const useAuthenticationsTrafficBarChart = ({
    filters
}: UseAuthenticationsTrafficBarChartProps): UseAuthenticationsTrafficBarChartReturn => {
    const [selectedTab, setSelectedTab] = useState<AuthenticationsTrafficTab>('mfa');

    const { mfaAllAuthenticationsTraffic, mfaAllAuthenticationsTrafficIsLoading } = useUniversalMfaMetrics(filters);

    const barChartVariant = selectedTab === 'all' ? 'stacked' : 'single';
    const isLoading = mfaAllAuthenticationsTrafficIsLoading;
    const rawData = mfaAllAuthenticationsTraffic;

    const barChartData = useMemo(() => {
        if (!rawData) return [];

        return rawData.map((item) => {
            if (selectedTab === 'all') {
                const allAuthenticationsTraffic = item as AllAuthenticationsTraffic;
                const hideTooltip = allAuthenticationsTraffic.total === 0 && allAuthenticationsTraffic.mfa === 0;
                return {
                    date: allAuthenticationsTraffic.date,
                    variant: 'stacked' as const,
                    value: allAuthenticationsTraffic.mfa,
                    stackedValue: allAuthenticationsTraffic.total - allAuthenticationsTraffic.mfa,
                    tooltipProps: hideTooltip ? undefined : {
                        title: getDateShortFormat(allAuthenticationsTraffic.date),
                        titleClassName: 'font-bold text-[10px] underline',
                        description: (
                            <MappingList
                                items={[
                                    {
                                        name: 'All authentications',
                                        value: allAuthenticationsTraffic.total,
                                        fill: ChartColors['data-bg-weak-4'],
                                        className: 'text-sf-white text-[10px] gap-1'
                                    },
                                    {
                                        name: 'MFA authentications',
                                        value: allAuthenticationsTraffic.mfa,
                                        fill: ChartColors['data-bg-main-4'],
                                        className: 'text-sf-white text-[10px] gap-1'
                                    }
                                ]}
                                isLoading={false}
                                containerClassName="[&]:gap-1"
                            />
                        )
                    }
                };
            }

            return {
                date: item.date,
                variant: 'single' as const,
                value: item.mfa,
                tooltipProps: item.mfa === 0 ? undefined : {
                    title: getDateShortFormat(item.date),
                    titleClassName: 'font-bold text-[10px] underline',
                    description: (
                        <MappingList
                            items={[
                                {
                                    name: 'MFA authentications',
                                    value: item.mfa,
                                    fill: ChartColors['data-bg-main-4'],
                                    className: 'text-sf-white text-[10px] gap-1'
                                }
                            ]}
                            isLoading={false}
                        />
                    )
                }
            };
        });
    }, [rawData, selectedTab]);

    const mappingList = useMemo(() => {
        if (!rawData) return [];

        if (selectedTab === 'all') {
            return [
                {
                    name: 'All authentications',
                    fill: ChartColors['data-bg-weak-4'],
                    value: 0
                },
                {
                    name: 'MFA authentications',
                    fill: ChartColors['data-bg-main-4'],
                    value: 0
                }
            ];
        }
        return [
            {
                name: 'MFA authentications',
                fill: ChartColors['data-bg-main-4'],
                value: 0
            }
        ];
    }, [rawData, selectedTab]);

    return {
        selectedTab,
        setSelectedTab,
        barChartData,
        isLoading,
        barChartVariant,
        mappingList
    };
};
